/**
 * Shared between the browser and the Netlify functions in netlify/functions.
 * Pure module: no Vite env reads, no fetch, no secrets. The secrets
 * (PINATA_JWT, TELEGRAM_BOT_TOKEN, TELEGRAM_CHAT_ID) only exist server-side.
 */

/** Called directly, not through /api/*: the SPA catch-all in public/_redirects would shadow it. */
export const PIN_FUNCTION_PATH = '/.netlify/functions/pinata-pin';
export const TELEGRAM_FUNCTION_PATH = '/.netlify/functions/telegram-notify';

/** Wallet session proof (the Sign-in message + signature already held by Web3AuthContext). */
export const SESSION_HEADER = 'x-sf-session';

const MB = 1024 * 1024;
/** Whole application envelope JSON. Netlify buffers request bodies up to ~6 MB. */
export const MAX_APPLICATION_JSON_BYTES = 5 * MB;
/** Raw document an athlete can attach (base64 inside the envelope grows it by ~1/3). */
export const MAX_APPLICATION_DOCUMENT_BYTES = 3 * MB;
export const MAX_TELEGRAM_BODY_BYTES = 16 * 1024;

export const APPLICATION_ROLES = ['athlete', 'agent', 'officer', 'representative'] as const;
export type ApplicationRole = (typeof APPLICATION_ROLES)[number];

export const CONTACT_CATEGORIES = [
  'General Inquiry',
  'Technical Support',
  'Partnership Inquiry',
  'Feedback',
] as const;

export const CONTACT_COUNTRIES = [
  'Belgium',
  'Curacao',
  'Dominican Republic',
  'Ireland',
  'Netherlands',
  'Portugal',
  'Scotland',
  'South Africa',
  'United Kingdom',
  'Zimbabwe',
  'Other',
] as const;

export const CONTACT_LIMITS = {
  name: 100,
  email: 254,
  telegram_handle: 64,
  message: 3000,
} as const;

/** Name of the hidden ContactForm field. Humans leave it empty; bots fill it. */
export const CONTACT_HONEYPOT_FIELD = 'website';

export interface SessionProof {
  message: string;
  signature: string;
}

export interface ContactNotifyPayload {
  kind: 'contact';
  name: string;
  email: string;
  telegram_handle?: string;
  category: string;
  country: string;
  message: string;
  website?: string;
}

export interface ApplicationNotifyPayload {
  kind: 'application';
  role: ApplicationRole;
  didId: number;
}

export type TelegramNotifyPayload = ContactNotifyPayload | ApplicationNotifyPayload;

function toBase64(binary: string): string {
  return (globalThis as unknown as { btoa(s: string): string }).btoa(binary);
}

function fromBase64(value: string): string {
  return (globalThis as unknown as { atob(s: string): string }).atob(value);
}

export function encodeSessionProof(proof: SessionProof): string {
  const bytes = new TextEncoder().encode(
    JSON.stringify({ message: proof.message, signature: proof.signature })
  );
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return toBase64(binary);
}

export function decodeSessionProof(value: string | null | undefined): SessionProof | null {
  if (!value || value.length > 4096) return null;
  try {
    const binary = fromBase64(value.trim());
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<SessionProof>;
    if (typeof parsed.message !== 'string' || typeof parsed.signature !== 'string') return null;
    return { message: parsed.message, signature: parsed.signature };
  } catch {
    return null;
  }
}

/** Same text as applicationSubmittedNotice in applicantCrypto.ts (no name/email/phone). */
export function applicationNoticeText(opts: { didId: number; role: string }): string {
  return `New SportsFuture ${opts.role} application (DID #${opts.didId}). Review in admin.`;
}
